import { formatInTimeZone } from 'date-fns-tz';
import type { TableData } from '../../design-system';
import { DateFormatDateFns } from '../dateFormat';
import { getAddressAsString, isAddress } from '../locations/address';
import type { Column } from './columns';

const { timeZone: defaultTimeZone } = Intl.DateTimeFormat().resolvedOptions();

type FieldValue = unknown;

export type FieldValueGetter<RowData extends TableData> = (
  rowData: TableRow<RowData>,
  column: Column<RowData>,
) => FieldValue;

export type TableRow<RowData> = RowData & { tableData: { id: number } };

function getCsvFormattedDate(args: { date: string | Date; dateFormat?: string; timeZone?: string }): string | Date {
  const { date, dateFormat, timeZone } = args;
  try {
    return formatInTimeZone(new Date(date), timeZone ?? defaultTimeZone, dateFormat ?? DateFormatDateFns.Short);
  } catch (e) {
    if (e instanceof RangeError) {
      console.warn('Error formatting date: ', e);
      return date;
    }
    throw e;
  }
}

export function getCsvCellValue<RowData extends TableData>(args: {
  columnType?: Column<RowData>['type'];
  columnField?: Column<RowData>['field'];
  dateFormat?: string;
  value: FieldValue;
  timeZone?: string;
}) {
  const { columnType, columnField, dateFormat, timeZone, value } = args;
  if (!value) {
    return value;
  }
  if (columnType === 'date' && (typeof value === 'string' || value instanceof Date)) {
    return getCsvFormattedDate({ date: value, dateFormat, timeZone });
  }
  if (columnField === 'address' && isAddress(value)) {
    return getAddressAsString(value);
  }
  return value;
}

export function getCsvColumns<RowData extends TableData>(columns: Column<RowData>[]): Column<RowData>[] {
  return columns.filter((col) => !col.hidden && col.field && col.export !== false);
}
